import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { useAuth } from '../../../context/AuthContext';
import supabase from '../../../supabase/supabase';
import SafeIcon from '../../../common/SafeIcon';
import { format } from 'date-fns';

const ParticipantCertificates = () => {
  const { user } = useAuth();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCerts = async () => {
      const { data } = await supabase
        .from('certificates_1741860000000')
        .select('*, programmes_1741860000000(name)')
        .eq('user_id', user.id);
      setCerts(data || []);
      setLoading(false);
    };
    fetchCerts();
  }, [user]);

  const downloadPDF = (cert) => {
    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(10, 17, 40); // Navy
    doc.rect(0, 0, 210, 40, 'F');
    
    doc.setTextColor(212, 175, 55); // Gold
    doc.setFontSize(24);
    doc.text("THE KAJIDORI COLLECTIVE", 105, 25, { align: 'center' });
    
    // Content
    doc.setTextColor(10, 17, 40);
    doc.setFontSize(18);
    doc.text("CERTIFICATE OF COMPLETION", 105, 60, { align: 'center' });
    
    doc.setFontSize(14);
    doc.text("This is to certify that", 105, 80, { align: 'center' });
    
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text(`${user.profile.first_name} ${user.profile.last_name}`, 105, 100, { align: 'center' });
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(14);
    doc.text("has successfully completed the programme", 105, 120, { align: 'center' });
    
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(cert.programmes_1741860000000.name, 105, 140, { align: 'center' });
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.text(`Issue Date: ${format(new Date(cert.issue_date), 'dd MMMM yyyy')}`, 105, 180, { align: 'center' });
    doc.text(`Reference: ${cert.reference_number}`, 105, 190, { align: 'center' });
    
    doc.save(`Certificate_${cert.reference_number}.pdf`);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <header className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-navy-900">My Certificates</h1>
        <p className="text-slate-500">Your professional recognition and training records.</p>
      </header>

      {loading ? (
        <div className="py-20 text-center">Loading certificates...</div>
      ) : certs.length === 0 ? (
        <div className="bg-white p-12 rounded-2xl border-2 border-dashed border-slate-200 text-center">
          <SafeIcon name="Award" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-navy-900 mb-2">No Certificates Yet</h3>
          <p className="text-slate-500">Your certificates will appear here once you complete a programme.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {certs.map((cert) => (
            <div key={cert.id} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm group hover:shadow-md transition-all">
              <div className="flex justify-between items-start mb-6">
                <div className="w-12 h-12 bg-navy-50 rounded-xl flex items-center justify-center">
                  <SafeIcon name="Award" className="w-6 h-6 text-navy-900" />
                </div>
                {cert.status === 'Completed' ? (
                  <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                    <SafeIcon name="Check" className="w-3 h-3" /> Completed
                  </span>
                ) : (
                  <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2.5 py-1 rounded-full">
                    {cert.progress_percent}% Complete
                  </span>
                )}
              </div>
              
              <h3 className="text-lg font-bold text-navy-900 mb-1">{cert.programmes_1741860000000.name}</h3>
              <p className="text-xs text-slate-500 mb-6">Issued: {format(new Date(cert.issue_date), 'dd MMM yyyy')}</p>
              
              {cert.status === 'Completed' ? (
                <button 
                  onClick={() => downloadPDF(cert)}
                  className="w-full flex items-center justify-center gap-2 bg-navy-900 text-white py-3 rounded-lg font-bold hover:bg-navy-800 transition-colors"
                >
                  <SafeIcon name="Download" className="w-4 h-4" /> Download Certificate (PDF)
                </button>
              ) : (
                <div className="w-full bg-slate-100 rounded-full h-2 mt-4">
                  <div className="bg-gold-500 h-2 rounded-full" style={{ width: `${cert.progress_percent}%` }}></div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ParticipantCertificates;